import React from "react";

/**
 * PrivacyPolicy — styled as a formal issued document (certificate/gazette
 * register aesthetic): parchment paper, engraved seal, letterhead metadata
 * block, and numbered "Articles" with lettered clauses.
 */

const styles = `
  .apv-page {
    min-height: 100vh;
    background: #e9e2d0;
    background-image:
      radial-gradient(circle at 15% 20%, rgba(124,42,42,0.04), transparent 45%),
      radial-gradient(circle at 85% 80%, rgba(124,42,42,0.04), transparent 45%);
    padding: 56px 20px;
    font-family: Georgia, "Times New Roman", Times, serif;
    color: #26221c;
  }

  .apv-frame {
    max-width: 860px;
    margin: 0 auto;
    background: #f8f3e6;
    border: 2px solid #26221c;
    padding: 6px;
    box-shadow: 0 12px 40px rgba(38,34,28,0.18);
  }

  .apv-frame-inner {
    border: 1px solid #b9ae95;
    padding: 48px 56px 40px;
    position: relative;
  }

  .apv-frame-inner::before {
    content: "";
    position: absolute;
    inset: 10px;
    border: 1px solid rgba(124,42,42,0.25);
    pointer-events: none;
  }

  /* Seal */
  .apv-seal {
    width: 92px;
    height: 92px;
    margin: 0 auto 18px;
    border-radius: 50%;
    background:
      radial-gradient(circle at 50% 50%, #f8f3e6 0 26px, transparent 27px),
      conic-gradient(from 0deg, #7c2a2a 0 8deg, transparent 8deg 22deg,
        #7c2a2a 22deg 30deg, transparent 30deg 44deg,
        #7c2a2a 44deg 52deg, transparent 52deg 66deg,
        #7c2a2a 66deg 74deg, transparent 74deg 88deg,
        #7c2a2a 88deg 96deg, transparent 96deg 110deg,
        #7c2a2a 110deg 118deg, transparent 118deg 132deg,
        #7c2a2a 132deg 140deg, transparent 140deg 154deg,
        #7c2a2a 154deg 162deg, transparent 162deg 176deg,
        #7c2a2a 176deg 184deg, transparent 184deg 198deg,
        #7c2a2a 198deg 206deg, transparent 206deg 220deg,
        #7c2a2a 220deg 228deg, transparent 228deg 242deg,
        #7c2a2a 242deg 250deg, transparent 250deg 264deg,
        #7c2a2a 264deg 272deg, transparent 272deg 286deg,
        #7c2a2a 286deg 294deg, transparent 294deg 308deg,
        #7c2a2a 308deg 316deg, transparent 316deg 330deg,
        #7c2a2a 330deg 338deg, transparent 338deg 352deg,
        #7c2a2a 352deg 360deg),
      radial-gradient(circle, #f8f3e6 0 30px, #7c2a2a 30px 34px, #a9822f 34px 36px, transparent 36px);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .apv-seal-mono {
    font-family: Georgia, serif;
    font-weight: 700;
    font-size: 20px;
    letter-spacing: 1px;
    color: #7c2a2a;
  }

  .apv-eyebrow {
    text-align: center;
    font-family: ui-monospace, "SF Mono", "Courier New", monospace;
    font-size: 11px;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: #6b6255;
    margin: 0 0 6px;
  }

  .apv-title {
    text-align: center;
    font-size: 30px;
    font-weight: 400;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #26221c;
    margin: 0 0 4px;
  }

  .apv-subtitle {
    text-align: center;
    font-size: 13px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #7c2a2a;
    margin: 0 0 26px;
  }

  .apv-rule {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 30px;
  }
  .apv-rule::before,
  .apv-rule::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #b9ae95;
  }
  .apv-rule-dot {
    width: 6px;
    height: 6px;
    background: #7c2a2a;
    transform: rotate(45deg);
  }

  .apv-meta {
    width: 100%;
    border-collapse: collapse;
    font-family: ui-monospace, "SF Mono", "Courier New", monospace;
    font-size: 12.5px;
    color: #4a4436;
    margin-bottom: 34px;
  }
  .apv-meta td {
    padding: 5px 0;
    border-bottom: 1px dotted #cfc6ac;
  }
  .apv-meta td:first-child {
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #6b6255;
    width: 220px;
  }

  .apv-preamble {
    font-style: italic;
    color: #4a4436;
    line-height: 1.75;
    margin-bottom: 32px;
    padding-left: 18px;
    border-left: 2px solid #b9ae95;
  }

  .apv-article {
    margin-top: 34px;
  }

  .apv-article-head {
    display: flex;
    align-items: baseline;
    gap: 12px;
    border-bottom: 1px solid #26221c;
    padding-bottom: 8px;
    margin-bottom: 14px;
  }

  .apv-article-num {
    font-family: ui-monospace, "SF Mono", "Courier New", monospace;
    font-size: 13px;
    color: #7c2a2a;
    letter-spacing: 1px;
    white-space: nowrap;
  }

  .apv-article-title {
    font-size: 16.5px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #26221c;
    margin: 0;
  }

  .apv-article p {
    line-height: 1.8;
    color: #2f2b23;
    margin: 0 0 14px;
    font-size: 15px;
  }

  .apv-clauses {
    list-style: none;
    margin: 0 0 16px;
    padding: 0;
  }

  .apv-clauses li {
    display: flex;
    gap: 10px;
    line-height: 1.75;
    color: #2f2b23;
    font-size: 15px;
    margin-bottom: 8px;
  }

  .apv-clause-mark {
    font-family: ui-monospace, "SF Mono", "Courier New", monospace;
    color: #7c2a2a;
    flex-shrink: 0;
  }

  .apv-footer {
    margin-top: 46px;
    padding-top: 24px;
    border-top: 2px double #26221c;
  }

  .apv-attest {
    text-align: center;
    font-size: 12px;
    letter-spacing: 1px;
    color: #6b6255;
    font-style: italic;
    margin-bottom: 10px;
  }

  .apv-copyright {
    text-align: center;
    font-family: ui-monospace, "SF Mono", "Courier New", monospace;
    font-size: 11px;
    letter-spacing: 1.5px;
    color: #6b6255;
    text-transform: uppercase;
  }

  @media (max-width: 640px) {
    .apv-frame-inner {
      padding: 34px 20px 28px;
    }
    .apv-title {
      font-size: 22px;
    }
    .apv-meta td:first-child {
      width: 150px;
    }
  }

  @media print {
    .apv-page {
      background: #fff;
      padding: 0;
    }
    .apv-frame {
      box-shadow: none;
    }
  }
`;

const articles = [
  {
    num: "I",
    title: "Information We Collect",
    intro:
      "Depending on the features you use, APLASEVAK may collect information such as:",
    clauses: [
      "Name",
      "Mobile number",
      "Address and ward information",
      "Complaint and service request details",
      "Suggestions and feedback",
      "Images or photographs submitted with complaints or requests",
      "Information related to your interactions within the application",
    ],
  },
  {
    num: "II",
    title: "OTP Authentication",
    paragraphs: [
      "APLASEVAK uses mobile-number-based OTP authentication to help verify user accounts and provide secure access to the application.",
      "OTPs are used for authentication and are not intended to be used for any other purpose.",
    ],
  },
  {
    num: "III",
    title: "How We Use Information",
    intro: "Information collected through APLASEVAK may be used to:",
    clauses: [
      "Create and manage your account",
      "Authenticate your identity",
      "Process complaints and service requests",
      "Process suggestions and feedback",
      "Communicate updates regarding submitted issues",
      "Help authorized municipal representatives respond to civic issues",
      "Improve the application's functionality and reliability",
    ],
  },
  {
    num: "IV",
    title: "Complaints, Requests and Suggestions",
    paragraphs: [
      "When you submit a complaint, service request, or suggestion, the information you provide may be made available to authorized personnel responsible for handling the relevant civic issue.",
      "This may include the description, location, ward, submitted images, and other information necessary to process the request.",
    ],
  },
  {
    num: "V",
    title: "Images and Photos",
    paragraphs: [
      "If you choose to attach photographs to a complaint or service request, those images may be uploaded to our systems and accessed by authorized personnel for the purpose of reviewing and resolving the reported issue.",
    ],
  },
  {
    num: "VI",
    title: "Data Security",
    paragraphs: [
      "We take reasonable technical and organizational measures to protect information handled through APLASEVAK against unauthorized access, alteration, disclosure, or misuse.",
      "However, no internet-based service can guarantee absolute security.",
    ],
  },
  {
    num: "VII",
    title: "Data Sharing",
    paragraphs: [
      "Information may be shared with authorized municipal representatives or personnel when necessary to process and resolve complaints, service requests, and suggestions.",
      "We do not intend to sell personal information to third parties.",
    ],
  },
  {
    num: "VIII",
    title: "Data Retention",
    paragraphs: [
      "Information may be retained for as long as reasonably necessary to provide the application's services, maintain records, resolve civic issues, comply with applicable requirements, and maintain the security of the platform.",
    ],
  },
  {
    num: "IX",
    title: "User Responsibilities",
    paragraphs: [
      "Users should provide accurate information and should not submit content that is unlawful, abusive, misleading, or unrelated to the civic service being requested.",
    ],
  },
  {
    num: "X",
    title: "Children's Privacy",
    paragraphs: [
      "APLASEVAK is intended for general civic-service use and is not specifically directed toward children. Users should provide information only when they are legally permitted to do so.",
    ],
  },
  {
    num: "XI",
    title: "Changes to This Privacy Policy",
    paragraphs: [
      "This Privacy Policy may be updated from time to time. Any updated version will be made available through this page with a revised update date.",
    ],
  },
  {
    num: "XII",
    title: "Contact Us",
    paragraphs: [
      "If you have questions regarding this Privacy Policy or the handling of your information, please contact the APLASEVAK support team.",
    ],
  },
];

const PrivacyPolicy = () => {
  return (
    <div className="apv-page">
      <style>{styles}</style>

      <div className="apv-frame">
        <div className="apv-frame-inner">
          <div className="apv-seal">
            <span className="apv-seal-mono">AP</span>
          </div>

          <p className="apv-eyebrow">Aplasevak Civic Services Platform</p>
          <h1 className="apv-title">Privacy Policy</h1>
          <p className="apv-subtitle">Issued Policy Document</p>

          <div className="apv-rule">
            <span className="apv-rule-dot" />
          </div>

          <table className="apv-meta">
            <tbody>
              <tr>
                <td>Document No.</td>
                <td>APS&ndash;PP&ndash;2026&ndash;08</td>
              </tr>
              <tr>
                <td>Effective Date</td>
                <td>August 14, 2026</td>
              </tr>
              <tr>
                <td>Version</td>
                <td>1.0</td>
              </tr>
              <tr>
                <td>Status</td>
                <td>In Force</td>
              </tr>
            </tbody>
          </table>

          <p className="apv-preamble">
            APLASEVAK is a civic services application designed to help
            citizens submit and track complaints, service requests, and
            suggestions, and to communicate with authorized municipal
            representatives. This document sets out the terms under which
            information is collected, used, and safeguarded.
          </p>

          {articles.map((a) => (
            <section className="apv-article" key={a.num}>
              <div className="apv-article-head">
                <span className="apv-article-num">Article {a.num}</span>
                <h2 className="apv-article-title">{a.title}</h2>
              </div>

              {a.intro && <p>{a.intro}</p>}

              {a.clauses && (
                <ul className="apv-clauses">
                  {a.clauses.map((c, i) => (
                    <li key={i}>
                      <span className="apv-clause-mark">
                        ({String.fromCharCode(97 + i)})
                      </span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              )}

              {a.paragraphs &&
                a.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </section>
          ))}

          <div className="apv-footer">
            <p className="apv-attest">
              This document is digitally issued and requires no signature.
            </p>
            <p className="apv-copyright">
              &copy; 2026 Aplasevak &middot; All Rights Reserved
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
